import { Helmet } from 'react-helmet-async';
import type { LocationSearchParams } from '../../hooks/search/useLocationSearchParams';
import type { Supplier } from '../../types/Supplier';
import { geoLine, stateAbbr } from '../location/profile/LocationProfileSEO';
import { DEFAULT_IMAGE, useClaimHead } from '../seo/HeadDefaults';

const SITE = 'https://secur.space';
const ITEM_LIST_MAX = 50;

interface SearchSEOProps {
  locationParams: LocationSearchParams | undefined;
  /** The yards currently listed on the page, in display order. */
  locations?: Supplier[];
}

export const searchCanonical = (city: string, state: string): string =>
  `${SITE}/search?city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}`;

/** Lead with what people type ("truck parking los angeles"), then the rest of the offer. */
export const searchTitle = (city: string, state: string): string =>
  `Truck Parking & Container Storage in ${city}, ${stateAbbr(state)} | SecurSpace`;

export const searchDescription = (city: string, state: string): string =>
  `Truck parking, trailer parking and container storage in ${city}, ${stateAbbr(state)}. Compare secure yards, daily and monthly prices, amenities and instant booking on SecurSpace.`;

/**
 * schema.org ItemList of the yards shown for a city, so Google reads the page
 * as a collection about that city rather than a generic search form. Capped so
 * the JSON stays small; the page itself still lists everything.
 */
export const buildSearchItemList = (city: string, state: string, locations: Supplier[]) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: geoLine(city, state),
  url: searchCanonical(city, state),
  numberOfItems: locations.length,
  itemListElement: locations.slice(0, ITEM_LIST_MAX).map((l, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    url: `${SITE}/location-profile/${l.locationId || l.id}`,
    name: l.locationName,
  })),
});

const SearchSEO = ({ locationParams, locations = [] }: SearchSEOProps) => {
  useClaimHead();
  const city = locationParams?.city || '';
  const state = locationParams?.state || '';
  const hasLocation = !!city && !!state;

  const title = hasLocation ? searchTitle(city, state) : 'Truck Parking & Container Storage Near You | SecurSpace';
  const description = hasLocation
    ? searchDescription(city, state)
    : 'Truck parking, trailer parking and container storage across the United States. Compare secure yards, prices and amenities and book on SecurSpace.';
  const canonicalUrl = hasLocation ? searchCanonical(city, state) : `${SITE}/search`;
  const keywords = hasLocation
    ? `truck parking ${city}, container storage ${stateAbbr(state)}, trailer parking, intermodal storage, ${city} ${stateAbbr(state)}`
    : 'truck parking, container storage, trailer parking, intermodal storage';

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="SecurSpace" />
      <meta property="og:image" content={DEFAULT_IMAGE} />
      {hasLocation && locations.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify(buildSearchItemList(city, state, locations))}
        </script>
      )}
    </Helmet>
  );
};

export default SearchSEO;
